import { useState, useEffect, useMemo, Fragment } from 'react';
import { Users, Search, Power, UserPlus, Shield, Mail, KeyRound, UserRoundPen, UserRoundX, MapPin } from 'lucide-react';
import { Combobox } from '../components/ui/Combobox';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { usuarioService } from '../services/usuarioService';
import { rolService } from '../services/rolService';
import { lumisialService } from '../services/lumisialService';
import { solicitudRolService, SolicitudRolWithDetails } from '../services';
import { authService } from '../services/authService';
import { Usuario, Rol } from '../types/auth';
import { matchesSearch } from '../utils/textNormalization';
import { Lumisial } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { useToast } from '../components/ui/Toast';
import { SolicitarRolModal } from '../components/SolicitarRolModal';
import { EliminarRolModal } from '../components/EliminarRolModal';
import { ResolverSolicitudRolModal } from '../components/ResolverSolicitudRolModal';
import { EnviarCredencialesModal } from '../components/EnviarCredencialesModal';
import { formatDate } from '../utils/formatters';
import { formatJerarquiaMiembro } from '../utils/miembroJerarquia';
import { canModifyUser } from '../utils/roleHierarchy';
import {
  puedeAprobarSolicitud, puedeResolverSolicitud, type SolicitudResolvible,
} from '../utils/resolucionSolicitudRol';
import { agruparUsuarios, displayNombreRol } from '../utils/agruparUsuarios';

const CICLO_ESTADO = ['activo', 'inactivo', ''] as const;

const ESTADO_FILTRO_LABEL: Record<string, string> = {
  activo: 'Activos',
  inactivo: 'Inactivos',
  '': 'Ambos',
};

const ESTADO_FILTRO_CLASS: Record<string, string> = {
  activo: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
  inactivo: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  '': 'bg-blue-100 text-blue-700 hover:bg-blue-200',
};

// Spinner inline para cambios de pestaña (no reemplaza toda la página)
const TabLoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500"></div>
  </div>
);

export const GestionUsuariosPage: React.FC = () => {
  const { usuario } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const initialTab = ((location.state as { activeTab?: string })?.activeTab || 'usuarios') as 'usuarios' | 'pendientes' | 'historial';
  const [activeTab, setActiveTab] = useState<'usuarios' | 'pendientes' | 'historial'>(initialTab);

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [lumisiales, setLumisiales] = useState<Lumisial[]>([]);
  const [solicitudes, setSolicitudes] = useState<SolicitudRolWithDetails[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [filtroRol, setFiltroRol] = useState<string>('');
  const [filtroLumisial, setFiltroLumisial] = useState<string>('');
  const [filtroEstado, setFiltroEstado] = useState<string>('activo');

  const [stats, setStats] = useState({ total: 0, activos: 0, inactivos: 0 });

  const [showSolicitarModal, setShowSolicitarModal] = useState(false);
  const [showCredencialesModal, setShowCredencialesModal] = useState(false);
  const [showResolverModal, setShowResolverModal] = useState(false);

  const [usuarioParaModificar, setUsuarioParaModificar] = useState<Usuario | null>(null);
  const [usuarioParaEliminar, setUsuarioParaEliminar] = useState<Usuario | null>(null);
  const [usuarioParaCredenciales, setUsuarioParaCredenciales] = useState<Usuario | null>(null);
  const [authInfo, setAuthInfo] = useState<{ hasLoggedIn: boolean; lastSignIn: string | null; hasCredentials: boolean } | null>(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState<SolicitudRolWithDetails | null>(null);

  const loadPendingCount = async () => {
    try {
      const count = await solicitudRolService.getCountPendientes();
      setPendingCount(count);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'usuarios') {
        const [usuariosData, rolesData, lumisialesData, statsData] = await Promise.all([
          usuarioService.getAll(),
          rolService.getAll(),
          lumisialService.getAll(),
          usuarioService.getStats(),
        ]);
        setUsuarios(usuariosData);
        setRoles(rolesData);
        setLumisiales(lumisialesData);
        setStats(statsData);
      } else if (activeTab === 'pendientes') {
        const data = await solicitudRolService.getPendientes();
        setSolicitudes(data);
      } else if (activeTab === 'historial') {
        const data = await solicitudRolService.getHistorial();
        setSolicitudes(data);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    loadPendingCount();
    const interval = setInterval(loadPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleModificarRol = (usuarioItem: Usuario) => { setUsuarioParaModificar(usuarioItem); };

  const handleEliminarRol = (usuarioItem: Usuario) => { setUsuarioParaEliminar(usuarioItem); };

  const handleEnviarCredenciales = async (usuario: Usuario) => {
    try {
      const info = await usuarioService.getUserAuthInfo(usuario.id);
      setAuthInfo(info);
      setUsuarioParaCredenciales(usuario);
      setShowCredencialesModal(true);
    } catch (error: unknown) {
      console.error('Error al obtener información de autenticación:', error);
      toast.error(error instanceof Error ? error.message : 'Error al obtener información del usuario');
    }
  };

  const handleCredencialesSuccess = () => {
    toast.success(authInfo?.hasCredentials ? 'Contraseña restablecida exitosamente' : 'Contraseña asignada exitosamente');
    loadData();
  };

  const handleConfirmarEmail = async (usuario: Usuario) => {
    try {
      await authService.confirmUserEmail(usuario.id);
      toast.success(
        `Email confirmado para ${usuario.nombres} ${usuario.apellidos}`
      );
      await loadData();
    } catch (error: unknown) {
      console.error('Error al confirmar email:', error);
      toast.error(error instanceof Error ? error.message : 'Error al confirmar el email del usuario');
    }
  };

  const handleResolverSolicitud = (solicitud: SolicitudRolWithDetails) => {
    setSelectedSolicitud(solicitud);
    setShowResolverModal(true);
  };

  const handleSolicitudSuccess = async () => {
    setShowResolverModal(false);
    setSelectedSolicitud(null);
    await loadData();
    await loadPendingCount();
  };

  // Resolver y aprobar son permisos distintos: denegar NO exige la tabla de aprobación de roles
  // (decisión de diseño 17 del spec), y así lo autoriza `resolver_solicitud_rol`. Gatear el botón
  // que abre el modal con la tabla escondía rechazos que el servidor sí acepta. La regla vive en
  // `utils/resolucionSolicitudRol` para poder probarla; acá solo se traduce la fila de la pantalla.
  const comoSolicitudResolvible = (solicitud: SolicitudRolWithDetails): SolicitudResolvible => ({
    estado: solicitud.estado,
    miembro_id: solicitud.miembro_id,
    solicitante_miembro_id: solicitud.solicitante_miembro_id,
    tipo_solicitud: solicitud.tipo_solicitud,
    rolSolicitado: solicitud.rol_solicitado?.nombre ?? null,
    rolAEliminar: solicitud.rol_a_eliminar?.nombre ?? null,
  });

  const puedeResolver = (solicitud: SolicitudRolWithDetails): boolean =>
    !!usuario && puedeResolverSolicitud(
      { miembroId: usuario.id, rol: usuario.rol.nombre },
      comoSolicitudResolvible(solicitud)
    );

  const puedeAprobar = (solicitud: SolicitudRolWithDetails): boolean =>
    !!usuario && puedeAprobarSolicitud(
      { miembroId: usuario.id, rol: usuario.rol.nombre },
      comoSolicitudResolvible(solicitud)
    );

  const usuariosFiltrados = usuarios.filter((usuario) => {
    const matchSearch =
      searchTerm === '' ||
      matchesSearch(usuario.nombres, searchTerm) ||
      matchesSearch(usuario.apellidos, searchTerm) ||
      matchesSearch(`${usuario.nombres} ${usuario.apellidos}`, searchTerm) ||
      matchesSearch(usuario.email, searchTerm);

    const matchRol = filtroRol === '' || usuario.rol_id === filtroRol;
    const matchLumisial = filtroLumisial === '' || usuario.lumisial_id === filtroLumisial;
    const matchEstado =
      filtroEstado === '' ||
      (filtroEstado === 'activo' && usuario.activo) ||
      (filtroEstado === 'inactivo' && !usuario.activo);

    return matchSearch && matchRol && matchLumisial && matchEstado;
  });

  const gruposUsuarios = useMemo(() => agruparUsuarios(usuariosFiltrados), [usuariosFiltrados]);

  const solicitudesFiltradas = solicitudes.filter((solicitud) => {
    if (searchTerm === '') return true;

    const nombreMiembro = `${solicitud.miembro.nombres} ${solicitud.miembro.apellidos}`;
    const nombreSolicitante = `${solicitud.solicitante_miembro.nombres} ${solicitud.solicitante_miembro.apellidos}`;
    const rol = solicitud.rol_solicitado?.nombre ?? solicitud.rol_a_eliminar?.nombre ?? '';

    return matchesSearch(nombreMiembro, searchTerm) ||
           matchesSearch(solicitud.miembro.email, searchTerm) ||
           matchesSearch(nombreSolicitante, searchTerm) ||
           matchesSearch(rol, searchTerm);
  });

  const cicloFiltroEstado = () => {
    const indiceActual = CICLO_ESTADO.indexOf(filtroEstado as typeof CICLO_ESTADO[number]);
    const siguiente = CICLO_ESTADO[(indiceActual + 1) % CICLO_ESTADO.length];
    setFiltroEstado(siguiente);
  };

  const getEstadoBadge = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return <Badge variant="warning">Pendiente</Badge>;
      case 'aprobada':
        return <Badge variant="success">Aprobada</Badge>;
      case 'rechazada':
        return <Badge variant="danger">Rechazada</Badge>;
      default:
        return null;
    }
  };

  const puedeCrearSolicitudes = usuario?.rol?.nombre?.toLowerCase().includes('admin') || false;

  const tabs = [
    {
      id: 'usuarios' as const,
      label: 'Lista de Usuarios',
    },
    {
      id: 'pendientes' as const,
      label: 'Solicitudes Pendientes',
      count: pendingCount,
    },
    {
      id: 'historial' as const,
      label: 'Historial de Solicitudes',
    },
  ];

  if (!initialLoaded && loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Usuarios</h2>
          <p className="text-slate-600">Administra usuarios, permisos y solicitudes de roles de la aplicación</p>
        </div>
        {puedeCrearSolicitudes && (
          <Button onClick={() => setShowSolicitarModal(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Solicitar Rol
          </Button>
        )}
      </div>

      {activeTab === 'usuarios' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-700">Total Usuarios</h3>
            </div>
            <p className="text-4xl font-bold text-slate-800">{stats.total}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Power className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-slate-700">Usuarios Activos</h3>
            </div>
            <p className="text-4xl font-bold text-green-600">{stats.activos}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Power className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-slate-700">Usuarios Inactivos</h3>
            </div>
            <p className="text-4xl font-bold text-red-600">{stats.inactivos}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={(tabId) => setActiveTab(tabId as typeof activeTab)} />
        </div>

        <div className="p-6">
          {loading && <TabLoadingSpinner />}
          {!loading && activeTab === 'usuarios' && (
            <>
              <div className="flex flex-col lg:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  />
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <Combobox
                    className="w-48"
                    options={roles.map((rol) => ({ value: rol.id, label: displayNombreRol(rol.nombre) }))}
                    value={filtroRol}
                    onChange={(v) => setFiltroRol(v)}
                    placeholder="Todos los roles"
                  />

                  <Combobox
                    className="w-56"
                    options={lumisiales.map((lumisial) => ({ value: lumisial.id, label: lumisial.nombre }))}
                    value={filtroLumisial}
                    onChange={(v) => setFiltroLumisial(v)}
                    placeholder="Todos los lumisiales"
                  />

                  <button
                    id="filtro-estado"
                    type="button"
                    onClick={cicloFiltroEstado}
                    title="Clic para cambiar el filtro de estado"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition cursor-pointer active:scale-[0.98] ${ESTADO_FILTRO_CLASS[filtroEstado]}`}
                  >
                    {ESTADO_FILTRO_LABEL[filtroEstado]}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Nombre</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Ubicación</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Estado</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-slate-500">
                          No se encontraron usuarios
                        </td>
                      </tr>
                    ) : (
                      gruposUsuarios.map((grupo) => (
                        <Fragment key={grupo.provincia}>
                          <tr className="bg-slate-100 border-y border-slate-200">
                            <td colSpan={5} className="py-2.5 px-4">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-500" />
                                <span className="font-bold text-slate-800">{grupo.provincia}</span>
                                <Badge variant="default">{grupo.total}</Badge>
                              </div>
                            </td>
                          </tr>
                          {grupo.subgrupos.map((subgrupo) => (
                            <Fragment key={`${grupo.provincia}-${subgrupo.rol.id}`}>
                              <tr className="bg-slate-50 border-b border-slate-100">
                                <td colSpan={5} className="py-2 pl-9 pr-4">
                                  <div className="flex items-center gap-2">
                                    <Shield className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="font-medium text-slate-600">{displayNombreRol(subgrupo.rol.nombre)}</span>
                                    <Badge variant="default">{subgrupo.usuarios.length}</Badge>
                                  </div>
                                </td>
                              </tr>
                              {subgrupo.usuarios.map((usuarioItem) => (
                        <tr key={usuarioItem.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800">
                              {usuarioItem.nombres} {usuarioItem.apellidos}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{usuarioItem.email}</td>
                          <td className="py-3 px-4 text-slate-600">
                            {(usuarioItem.lumisial
                              ? formatJerarquiaMiembro({ ...usuarioItem.lumisial, provincia_eclesiastica: undefined })
                              : '') || 'Sin ubicación registrada'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={usuarioItem.activo ? 'success' : 'default'}>
                              {usuarioItem.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEnviarCredenciales(usuarioItem)}
                                className="p-2 rounded-lg transition-colors hover:bg-blue-50 text-blue-600"
                                title={usuarioItem.user_id ? 'Restablecer contraseña' : 'Asignar contraseña'}
                                aria-label={usuarioItem.user_id ? 'Restablecer contraseña' : 'Asignar contraseña'}
                              >
                                <KeyRound className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleConfirmarEmail(usuarioItem)}
                                disabled={!canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')}
                                className={`p-2 rounded-lg transition-colors ${
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'text-slate-400 cursor-not-allowed'
                                    : 'hover:bg-blue-50 text-blue-600'
                                }`}
                                title={
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'No puedes confirmar usuarios de nivel igual o superior'
                                    : 'Confirmar email del usuario'
                                }
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { handleModificarRol(usuarioItem); }}
                                disabled={!canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')}
                                className={`p-2 rounded-lg transition-colors ${
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'text-slate-400 cursor-not-allowed'
                                    : 'hover:bg-blue-50 text-blue-600'
                                }`}
                                title={
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'No puedes modificar usuarios de nivel igual o superior'
                                    : 'Modificar Rol'
                                }
                              >
                                <UserRoundPen className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { handleEliminarRol(usuarioItem); }}
                                disabled={!canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')}
                                className={`p-2 rounded-lg transition-colors ${
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'text-slate-400 cursor-not-allowed'
                                    : 'hover:bg-red-50 text-red-600'
                                }`}
                                title={
                                  !canModifyUser(usuario?.rol?.nombre || '', usuarioItem.rol?.nombre || '')
                                    ? 'No puedes modificar usuarios de nivel igual o superior'
                                    : 'Eliminar Rol'
                                }
                              >
                                <UserRoundX className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                              ))}
                            </Fragment>
                          ))}
                        </Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!loading && activeTab !== 'usuarios' && (
            <>
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por miembro, solicitante o rol..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                {solicitudesFiltradas.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">
                      {searchTerm ? 'No se encontraron solicitudes' : 'No hay solicitudes'}
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Miembro
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Rol Solicitado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Solicitante
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Contexto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {solicitudesFiltradas.map((solicitud) => (
                        <tr key={solicitud.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {solicitud.miembro.nombres} {solicitud.miembro.apellidos}
                              </div>
                              <div className="text-sm text-slate-500">{solicitud.miembro.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {solicitud.tipo_solicitud === 'eliminacion' ? (
                              <Badge variant="danger">Eliminar: {solicitud.rol_a_eliminar?.nombre ?? 'Rol'}</Badge>
                            ) : (
                              <Badge variant="info">{solicitud.rol_solicitado?.nombre ?? '—'}</Badge>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {solicitud.solicitante_miembro.nombres} {solicitud.solicitante_miembro.apellidos}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {solicitud.provincia_eclesiastica?.nombre ||
                              solicitud.diocesis?.nombre ||
                              solicitud.lumisial?.nombre ||
                              'Global'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {formatDate(
                              activeTab === 'pendientes'
                                ? solicitud.fecha_solicitud
                                : solicitud.fecha_resolucion || solicitud.fecha_solicitud
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getEstadoBadge(solicitud.estado)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {activeTab === 'pendientes' && puedeResolver(solicitud) && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleResolverSolicitud(solicitud)}
                              >
                                Resolver
                              </Button>
                            )}
                            {activeTab === 'historial' && (
                              <div className="text-xs text-slate-500">
                                {solicitud.resuelto_por_miembro && (
                                  <>
                                    Por: {solicitud.resuelto_por_miembro.nombres}{' '}
                                    {solicitud.resuelto_por_miembro.apellidos}
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <SolicitarRolModal
        isOpen={showSolicitarModal}
        onClose={() => setShowSolicitarModal(false)}
        onSuccess={async () => {
          await loadData();
          await loadPendingCount();
        }}
      />

      {showCredencialesModal && usuarioParaCredenciales && authInfo && (
        <EnviarCredencialesModal
          usuario={usuarioParaCredenciales}
          authInfo={authInfo}
          onClose={() => {
            setShowCredencialesModal(false);
            setUsuarioParaCredenciales(null);
            setAuthInfo(null);
          }}
          onSuccess={handleCredencialesSuccess}
        />
      )}

      {showResolverModal && selectedSolicitud && (
        <ResolverSolicitudRolModal
          solicitud={selectedSolicitud}
          puedeAprobar={puedeAprobar(selectedSolicitud)}
          onClose={() => {
            setShowResolverModal(false);
            setSelectedSolicitud(null);
          }}
          onSuccess={handleSolicitudSuccess}
        />
      )}

      {usuarioParaModificar && (
        <SolicitarRolModal
          isOpen={!!usuarioParaModificar}
          miembroPreseleccionado={{
            id: usuarioParaModificar.id,
            rolActualId: usuarioParaModificar.rol_id,
            rolActualNombre: usuarioParaModificar.rol.nombre,
          }}
          onClose={() => { setUsuarioParaModificar(null); }}
          onSuccess={async () => {
            await loadData();
            await loadPendingCount();
          }}
        />
      )}

      {usuarioParaEliminar && (
        <EliminarRolModal
          isOpen={!!usuarioParaEliminar}
          usuario={usuarioParaEliminar}
          onClose={() => { setUsuarioParaEliminar(null); }}
          onSuccess={async () => {
            await loadData();
            await loadPendingCount();
          }}
        />
      )}

    </div>
  );
};
